




































import CashFlowReportMappingCard from "@/components/CashFlowReportMapping/CashFlowReportMappingCard.vue";
import useCoa from "@/hooks/useCoa";
import MNotificationVue from "@/mixins/MNotification.vue";
import { RequestQueryParams } from "@/models/class/request-query-params.class";
import { CashFlowSection as CashFlowSectionEnum } from "@/models/enums/CashFlowSection.enum";
import { CashFlowTransactionType } from "@/models/enums/CashFlowTransactionType.enum";
import {
  AccountOption,
  CashFlowCategoryRequestDto,
  CashFlowCategoryResponseDto,
  CashFlowMapping,
  CashFlowSection,
} from "@/models/interface/cash-flow-report-mapping";
import { cashFlowMappingService } from "@/services/CashFlowMapping.service";
import { Component, Mixins } from "vue-property-decorator";

const { findAllChildAccount } = useCoa();

@Component({
  components: {
    CashFlowReportMappingCard,
  },
})
export default class CashFlowReportMapping extends Mixins(MNotificationVue) {
  private readonly SECTION_ORDER = Object.values(CashFlowSectionEnum);

  public isLoading = false;
  public originalSections: CashFlowSection[] = [];
  sections: CashFlowSection[] = [];
  accountOptions: AccountOption[] = [];
  newlyAddedIndex: { section: string; index: number } | null = null;

  async created(): Promise<void> {
    this.isLoading = true;
    try {
      const params = new RequestQueryParams("", 0, "", 9999);
      const [mappingsData, accountsData] = await Promise.all([
        cashFlowMappingService.findAll(),
        findAllChildAccount(params),
      ]);
      this.originalSections = this.toSections(mappingsData);
      this.sections = this.deepClone(this.originalSections);
      this.accountOptions = this.toAccountOptions(accountsData.data);
    } catch (error) {
      this.showNotifError("notif_loading_failed");
      this.originalSections = this.toSections([]);
      this.sections = this.deepClone(this.originalSections);
      this.accountOptions = [];
    } finally {
      this.isLoading = false;
    }
  }

  private toSections(data: CashFlowCategoryResponseDto[]): CashFlowSection[] {
    if (!data || data.length === 0) {
      return this.SECTION_ORDER.map(section => ({
        id: section,
        title: section,
        mappings: [],
      }));
    }
    return this.SECTION_ORDER.map(section => {
      let localId = 1;
      const mappings: CashFlowMapping[] = data
        .filter(item => item.section === section)
        .map(item => ({
          id: localId++,
          name: item.name,
          accountSecureIds: item.accounts.map(a => a.id),
          transactionTypes: item.transactionTypes || [],
          isNew: false,
        }));
      return {
        id: section,
        title: section,
        mappings,
      };
    });
  }

  private toRequestDtos(
    sections: CashFlowSection[]
  ): CashFlowCategoryRequestDto[] {
    return sections.flatMap(section =>
      section.mappings.map(mapping => ({
        section: section.title as CashFlowSectionEnum,
        name: mapping.name,
        accountSecureIds: mapping.accountSecureIds,
        transactionTypes: mapping.transactionTypes || [],
      }))
    );
  }

  private deepClone<T>(data: T): T {
    return JSON.parse(JSON.stringify(data));
  }

  private toAccountOptions(data: any[]): AccountOption[] {
    return data.map(item => ({
      id: item.id,
      label: `${item.code} - ${item.description}`,
    }));
  }

  findSection(titleName: string): CashFlowSection | undefined {
    return this.sections.find(s => s.title === titleName);
  }

  handleAddRow(section: string): void {
    const sectionTarget = this.findSection(section);
    if (!sectionTarget) return;

    sectionTarget.mappings.push({
      id: Date.now(),
      name: "",
      accountSecureIds: [],
      transactionTypes: [],
      isNew: true,
    });

    const newIndex = sectionTarget.mappings.length - 1;
    this.newlyAddedIndex = { section, index: newIndex };

    this.$nextTick(() => {
      this.newlyAddedIndex = null;
    });
  }

  handleSave(payload: {
    section: string;
    index: number;
    name: string;
    accountSecureIds: string[];
    transactionTypes: CashFlowTransactionType[];
  }): void {
    const sectionTarget = this.findSection(payload.section);
    if (sectionTarget) {
      sectionTarget.mappings[payload.index].name = payload.name;
      sectionTarget.mappings[payload.index].accountSecureIds =
        payload.accountSecureIds;
      sectionTarget.mappings[payload.index].transactionTypes =
        payload.transactionTypes;
    }
  }

  handleDelete(payload: { section: string; index: number }): void {
    const sectionTarget = this.findSection(payload.section);
    if (sectionTarget) {
      sectionTarget.mappings.splice(payload.index, 1);
    }
  }

  handleCancel(payload: { section: string; index: number }): void {
    const sectionTarget = this.findSection(payload.section);
    if (!sectionTarget) return;

    const mapping = sectionTarget.mappings[payload.index];

    const isNewRow = Boolean(mapping.isNew);

    if (isNewRow) {
      sectionTarget.mappings.splice(payload.index, 1);
    } else {
      const originalSection = this.originalSections.find(
        s => s.title === payload.section
      );
      if (originalSection && originalSection.mappings[payload.index]) {
        sectionTarget.mappings[payload.index] = {
          ...originalSection.mappings[payload.index],
        };
      }
    }
  }

  handleDiscard(): void {
    this.$confirm({
      title: this.$t("lbl_confirmation"),
      content: this.$t("lbl_modal_discard_title_confirm"),
      onOk: () => {
        this.sections = this.deepClone(this.originalSections);
      },
    });
  }

  async handleSaveChanges(): Promise<void> {
    this.isLoading = true;
    try {
      const payload = this.toRequestDtos(this.sections);
      await cashFlowMappingService.save(payload);
      this.originalSections = this.deepClone(this.sections);
      this.showNotifSuccess("notif_mapping_save_success");
    } catch (error) {
      this.showNotifError("notif_mapping_save_failed");
    } finally {
      this.isLoading = false;
    }
  }
}
